import React, { useState } from 'react';
import { 
  Card, Button, Space, Table, message, Row, Col, 
  Input, Select, Modal, Form, Tag, Alert, Tooltip, Spin
} from 'antd';
import { 
  CopyOutlined, WalletOutlined, SearchOutlined,
  DeleteOutlined, LinkOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';

// 接口定义
interface AllowanceEntry {
  id: number;
  ownerAddress: string;
  allowance: string;
  status: 'idle' | 'loading' | 'success' | 'error';
  isSelected: boolean;
}

interface ConfigState {
  rpcUrl: string;
  tokenContractAddress: string;
  spenderAddress: string;
}

interface TokenInfo {
  symbol: string;
  decimals: number;
}

interface QueryProgress {
  checked: number;
  total: number;
}

// 预设网络配置
const NETWORK_CONFIGS = [
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH',
    explorer: 'https://etherscan.io/address/'
  },
  {
    name: 'BNB Smart Chain',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB',
    explorer: 'https://bscscan.com/address/'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC',
    explorer: 'https://polygonscan.com/address/'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH',
    explorer: 'https://arbiscan.io/address/'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH',
    explorer: 'https://basescan.org/address/'
  },
];

// ERC20 ABI (包含allowance函数)
const ERC20_ABI = [
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const AllowanceChecker: React.FC = () => {
  // 状态管理
  const [allowanceEntries, setAllowanceEntries] = useState<AllowanceEntry[]>([]);
  const [config, setConfig] = useState<ConfigState>({
    rpcUrl: NETWORK_CONFIGS[0].rpcUrl,
    tokenContractAddress: '',
    spenderAddress: ''
  });
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryProgress, setQueryProgress] = useState<QueryProgress>({ checked: 0, total: 0 });
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [currentExplorer, setCurrentExplorer] = useState(NETWORK_CONFIGS[0].explorer);
  
  // Modal 状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [addressesInput, setAddressesInput] = useState('');

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 监听网络变化，更新浏览器
  React.useEffect(() => {
    const network = NETWORK_CONFIGS.find(n => n.rpcUrl === config.rpcUrl);
    if (network) {
      setCurrentExplorer(network.explorer);
    }
  }, [config.rpcUrl]);

  // 导入钱包地址
  const handleImportWallets = () => {
    if (!addressesInput.trim()) {
      message.error('请输入钱包地址');
      return;
    }

    const addresses = addressesInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const validAddresses: string[] = [];
    const invalidAddresses: string[] = [];

    addresses.forEach((address, index) => {
      if (isValidAddress(address)) {
        validAddresses.push(address);
      } else {
        invalidAddresses.push(`第${index + 1}行`);
      }
    });

    if (invalidAddresses.length > 0) {
      message.error(`发现${invalidAddresses.length}个无效地址: ${invalidAddresses.join(', ')}`);
      return;
    }

    if (validAddresses.length === 0) {
      message.error('没有有效的地址');
      return;
    }

    // 去重处理
    const existingAddresses = new Set(allowanceEntries.map(e => e.ownerAddress.toLowerCase()));
    const newAddresses = validAddresses.filter(addr => 
      !existingAddresses.has(addr.toLowerCase())
    );

    if (newAddresses.length === 0) {
      message.warning('所有地址都已存在');
      return;
    }

    const newEntries: AllowanceEntry[] = newAddresses.map((address, index) => ({
      id: Date.now() + index,
      ownerAddress: address,
      allowance: '0',
      status: 'idle',
      isSelected: false
    }));

    setAllowanceEntries(prev => [...prev, ...newEntries]);
    setIsImportModalVisible(false);
    setAddressesInput('');
    message.success(`成功导入${newEntries.length}个地址`);
  };

  // 删除选中的地址
  const handleDeleteSelected = () => {
    const selectedCount = allowanceEntries.filter(e => e.isSelected).length;
    if (selectedCount === 0) {
      message.warning('没有选中的地址');
      return;
    }

    setAllowanceEntries(prev => prev.filter(entry => !entry.isSelected));
    message.success(`已删除${selectedCount}个地址`);
  };

  // 批处理工具函数
  const processInBatches = async <T,>(
    items: T[],
    batchSize: number,
    processFn: (item: T) => Promise<void>
  ): Promise<void> => {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await Promise.all(batch.map(item => processFn(item)));
    }
  };

  // 单个授权查询函数
  const fetchAllowanceForEntry = async (entry: AllowanceEntry): Promise<void> => {
    try {
      // 更新状态为加载中
      setAllowanceEntries(prev => prev.map(e =>
        e.id === entry.id ? { ...e, status: 'loading' } : e
      ));

      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const tokenContract = new ethers.Contract(config.tokenContractAddress, ERC20_ABI, provider);

      // 执行核心调用
      const rawAllowance = await tokenContract.allowance(entry.ownerAddress, config.spenderAddress);

      // 格式化结果
      let formattedAllowance: string;
      if (rawAllowance === ethers.MaxUint256) {
        formattedAllowance = "Unlimited";
      } else {
        formattedAllowance = ethers.formatUnits(rawAllowance, tokenInfo?.decimals || 18);
      }

      // 更新授权数量和状态
      setAllowanceEntries(prev => prev.map(e =>
        e.id === entry.id ? {
          ...e,
          allowance: formattedAllowance,
          status: 'success'
        } : e
      ));

    } catch (error) {
      console.error(`查询授权失败 ${entry.ownerAddress}:`, error);

      // 更新状态为错误
      setAllowanceEntries(prev => prev.map(e =>
        e.id === entry.id ? { ...e, status: 'error', allowance: '0' } : e
      ));
    } finally {
      // 更新进度
      setQueryProgress(prev => ({ ...prev, checked: prev.checked + 1 }));
    }
  };

  // 核心查询授权功能
  const handleCheckAllowances = async () => {
    // 启动与验证
    if (allowanceEntries.length === 0) {
      message.warning('请先导入钱包地址');
      return;
    }

    if (!isValidAddress(config.tokenContractAddress)) {
      message.error('请输入有效的代币合约地址');
      return;
    }

    if (!isValidAddress(config.spenderAddress)) {
      message.error('请输入有效的授权地址');
      return;
    }

    setIsQuerying(true);
    setQueryProgress({ checked: 0, total: allowanceEntries.length });

    // 重置所有条目状态
    setAllowanceEntries(prev => prev.map(entry => ({
      ...entry,
      status: 'idle',
      allowance: '0'
    })));

    try {
      message.info('开始批量查询授权任务...');

      // 获取代币信息
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const tokenContract = new ethers.Contract(config.tokenContractAddress, ERC20_ABI, provider);

      try {
        const [symbol, decimals] = await Promise.all([
          tokenContract.symbol(),
          tokenContract.decimals()
        ]);

        setTokenInfo({ symbol, decimals: Number(decimals) });
        message.success(`获取代币信息成功: ${symbol}`);
      } catch (error) {
        console.error('获取代币信息失败:', error);
        message.error('获取代币信息失败，请检查代币合约地址');
        setIsQuerying(false);
        return;
      }

      // 执行批量查询 (批处理大小设为20)
      await processInBatches(allowanceEntries, 20, fetchAllowanceForEntry);

      message.success('授权查询任务完成');
    } catch (error) {
      console.error('批量查询失败:', error);
      message.error('批量查询失败');
    } finally {
      setIsQuerying(false);
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        批量查询授权工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            style={buttonStyle}
          >
            删除选中
          </Button>
        </Space>
      </Card>

      {/* 授权信息表格 */}
      {allowanceEntries.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              钱包列表 ({allowanceEntries.length}个)
            </h3>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setAllowanceEntries([]);
                setQueryProgress({ checked: 0, total: 0 });
              }}
              style={buttonStyle}
            >
              清空全部
            </Button>
          </div>

          <Table
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '钱包地址',
                dataIndex: 'ownerAddress',
                width: 300,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '地址')}
                      />
                    </Tooltip>
                    <Tooltip title="在区块链浏览器中查看">
                      <Button
                        type="text"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(`${currentExplorer}${text}`, '_blank')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: '授权数量',
                dataIndex: 'allowance',
                width: 200,
                render: (text: string, record: AllowanceEntry) => {
                  if (record.status === 'loading') {
                    return <Spin size="small" />;
                  }

                  if (record.status === 'error') {
                    return <Tag color="error">查询失败</Tag>;
                  }

                  if (record.status === 'success') {
                    const allowanceValue = parseFloat(text);
                    if (allowanceValue === 0 || text === '0') {
                      return <Tag color="success">无授权</Tag>;
                    } else if (text === 'Unlimited') {
                      return <Tag color="error">无限授权</Tag>;
                    } else {
                      return (
                        <Tag color="error">
                          {allowanceValue.toLocaleString()} {tokenInfo?.symbol || ''}
                        </Tag>
                      );
                    }
                  }

                  return <Tag color="default">待查询</Tag>;
                },
              },
              {
                title: '操作',
                width: 80,
                render: (_, record: AllowanceEntry) => (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setAllowanceEntries(prev => prev.filter(entry => entry.id !== record.id));
                    }}
                  />
                ),
              },
            ]}
            dataSource={allowanceEntries}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: allowanceEntries.filter(e => e.isSelected).map(e => e.id),
              onChange: (selectedRowKeys) => {
                setAllowanceEntries(prev => prev.map(entry => ({
                  ...entry,
                  isSelected: selectedRowKeys.includes(entry.id)
                })));
              },
              onSelectAll: (selected) => {
                setAllowanceEntries(prev => prev.map(entry => ({
                  ...entry,
                  isSelected: selected
                })));
              },
              onSelect: (record, selected) => {
                setAllowanceEntries(prev => prev.map(entry =>
                  entry.id === record.id ? { ...entry, isSelected: selected } : entry
                ));
              },
            }}
            pagination={false}
            scroll={{ x: 800, y: 400 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {allowanceEntries.filter(e => e.isSelected).length} 个</strong>
              </Col>
              <Col span={8}>
                <strong>查询进度: {queryProgress.checked} / {queryProgress.total}</strong>
              </Col>
              <Col span={8}>
                <strong>
                  有授权: {allowanceEntries.filter(e =>
                    e.status === 'success' &&
                    (e.allowance === 'Unlimited' || parseFloat(e.allowance) > 0)
                  ).length} 个
                </strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 底部配置与操作区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          查询配置
        </h3>

        {/* 选择链 / 节点 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h4>
              <Select
                value={config.rpcUrl}
                onChange={(value) => {
                  setConfig(prev => ({ ...prev, rpcUrl: value }));
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
              >
                {NETWORK_CONFIGS.map(network => (
                  <Select.Option key={network.rpcUrl} value={network.rpcUrl}>
                    {network.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义RPC节点
              </h4>
              <Input
                value={config.rpcUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, rpcUrl: e.target.value }))}
                style={componentStyle}
                placeholder="输入自定义RPC URL"
              />
            </div>
          </Col>
        </Row>

        {/* 代币合约地址 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                代币合约地址 *
              </h4>
              <Input
                value={config.tokenContractAddress}
                onChange={(e) => setConfig(prev => ({ ...prev, tokenContractAddress: e.target.value }))}
                style={componentStyle}
                placeholder="输入ERC20代币合约地址"
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                授权地址 (Spender) *
                <Tooltip title="通常是您与之交互的DApp、DEX或跨链桥的合约地址">
                  <InfoCircleOutlined style={{ marginLeft: '4px', color: '#1890ff' }} />
                </Tooltip>
              </h4>
              <Input
                value={config.spenderAddress}
                onChange={(e) => setConfig(prev => ({ ...prev, spenderAddress: e.target.value }))}
                style={componentStyle}
                placeholder="输入被授权的合约地址"
              />
            </div>
          </Col>
        </Row>

        {/* 汇总与执行 */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={16}>
            <div style={{
              padding: '12px',
              background: '#fff7e6',
              borderRadius: '6px',
              border: '1px solid #ffd591'
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  🔍 查询统计
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  总地址: {allowanceEntries.length} 个 |
                  已查询: {queryProgress.checked} 个 |
                  剩余: {queryProgress.total - queryProgress.checked} 个
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  有授权地址: <strong style={{ color: '#fa8c16' }}>
                    {allowanceEntries.filter(e =>
                      e.status === 'success' &&
                      (e.allowance === 'Unlimited' || parseFloat(e.allowance) > 0)
                    ).length} 个
                  </strong>
                </div>
                {tokenInfo && (
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    查询代币: <strong>{tokenInfo.symbol}</strong>
                  </div>
                )}
              </Space>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleCheckAllowances}
                disabled={
                  allowanceEntries.length === 0 ||
                  !config.tokenContractAddress ||
                  !config.spenderAddress ||
                  isQuerying
                }
                loading={isQuerying}
                style={{
                  ...primaryButtonStyle,
                  minWidth: '160px',
                  height: '44px',
                  fontSize: '16px'
                }}
              >
                {isQuerying ? '查询中...' : '查询授权'}
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 导入钱包地址模态框 */}
      <Modal
        title="导入钱包地址"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setAddressesInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="批量导入钱包地址"
          description="请粘贴您的钱包地址（Owner），每行一个。系统会查询这些地址对指定合约的授权情况。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="钱包地址列表 (每行一个地址)"
            required
          >
            <Input.TextArea
              value={addressesInput}
              onChange={(e) => setAddressesInput(e.target.value)}
              placeholder="请粘贴钱包地址，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>输入您的钱包地址（Owner）来查询对特定合约的授权情况</li>
            <li>代币合约地址：要查询授权的ERC20代币合约</li>
            <li>授权地址（Spender）：被授权使用代币的合约地址，如DEX、跨链桥等</li>
            <li>绿色标签表示无授权，红色标签表示有授权（需要注意安全）</li>
            <li>无限授权显示为"Unlimited"，建议及时撤销以保护资产安全</li>
          </ul>
        </div>
      </Card>

      {/* 安全提示 */}
      <Card size="small" style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        marginTop: '12px'
      }}>
        <div style={{ color: '#ff4d4f' }}>
          <strong>⚠️ 安全提醒：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>定期检查和撤销不必要的代币授权，特别是无限授权</li>
            <li>只对信任的合约进行授权，避免授权给未知或可疑的合约</li>
            <li>使用完DApp后，建议及时撤销相关授权</li>
            <li>发现异常授权时，请立即撤销并转移资产到安全地址</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default AllowanceChecker;
